/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { useDispatch } from 'react-redux';
import { useState, useCallback } from 'react';
import { SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '../../../definitions/devices';
import { isPermissionDenied } from '../utils';
import { VideoProviderType } from '../types';
import { addGenericErrorNotification } from '../../../actions/pageActions';

// local tracks should include audio and video track
export const LOCAL_TRACKS_LENGTH = 2;

export function useLocalTracks(videoProvider: VideoProviderType) {
  const dispatch = useDispatch();
  const [audioTrack, setAudioTrack] = useState<VideoProvider.ILocalAudioTrack>();
  const [videoTrack, setVideoTrack] = useState<VideoProvider.ILocalVideoTrack>();
  const [isFetchingLocalTracks, setIsFetchingLocalTracks] = useState(false);

  const removeLocalVideoTrack = useCallback(() => {
    if (!videoTrack) {
      return;
    }
    videoTrack.stop();
    setVideoTrack(undefined);
  }, [videoTrack]);

  const removeLocalAudioTrack = useCallback(() => {
    if (audioTrack) {
      audioTrack.stop();
      setAudioTrack(undefined);
    }
  }, [audioTrack]);

  const getLocalVideoTrack = useCallback(async () => {
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

    const newTrack = await videoProvider.createLocalVideoTrack(selectedVideoDeviceId);
    setVideoTrack(newTrack);
    return newTrack;
  }, []);

  const getLocalAudioTrack = useCallback(async () => {
    const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);

    const newTrack = await videoProvider.createLocalAudioTrack(selectedAudioDeviceId);

    setAudioTrack(newTrack);
    return newTrack;
  }, []);

  const getAudioAndVideoTracks = useCallback(async () => {
    if (isFetchingLocalTracks || audioTrack || videoTrack) {
      return Promise.resolve();
    }
    setIsFetchingLocalTracks(true);

    const selectedAudioDeviceId = window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY);
    const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);

    // In Chrome, it is possible to deny permissions to only audio or only video.
    // If that has happened, then we don't want to attempt to acquire the device.
    const isCameraPermissionDenied = await isPermissionDenied('camera');
    const isMicrophonePermissionDenied = await isPermissionDenied('microphone');

    try {
      const { videoTrack, audioTrack, updatedVideoDeviceId, updatedAudioDeviceId } =
        await videoProvider.createLocalTracks(
          selectedAudioDeviceId,
          selectedVideoDeviceId,
          isCameraPermissionDenied,
          isMicrophonePermissionDenied,
        );

      if (videoTrack) {
        setVideoTrack(videoTrack);
        // Save the deviceId so it can be picked up by the VideoInputList component. This only matters
        // in cases where the user's video is disabled.
        window.localStorage.setItem(SELECTED_VIDEO_INPUT_KEY, updatedVideoDeviceId || 'default');
      }
      if (audioTrack) {
        setAudioTrack(audioTrack);

        // Save the deviceId so it can be picked up by the AudioInputList component.
        window.localStorage.setItem(SELECTED_AUDIO_INPUT_KEY, updatedAudioDeviceId || 'default');
      }

      const permissionError =
        isCameraPermissionDenied && isMicrophonePermissionDenied
          ? 'CameraAndMicrophonePermissionsDenied'
          : isCameraPermissionDenied
          ? 'CameraPermissionsDenied'
          : isMicrophonePermissionDenied
          ? 'MicrophonePermissionsDenied'
          : null;

      if (permissionError) {
        throw new Error(permissionError);
      }
    } catch (err: any) {
      const deniedDevices = ['Microphone', 'Camera'].filter(device => err.message?.includes(device));

      if (deniedDevices.length > 0) {
        const deviceNames = deniedDevices.join(' and ').toLowerCase();
        dispatch(
          addGenericErrorNotification(
            `You have denied access to your ${deviceNames}. Please allow permissions and refresh the page.`,
            true,
          ),
        );
      }
      throw err;
    } finally {
      setIsFetchingLocalTracks(false);
    }
  }, [audioTrack, videoTrack, isFetchingLocalTracks]);

  getAudioAndVideoTracks();
  const localTracks: (VideoProvider.ILocalAudioTrack | VideoProvider.ILocalVideoTrack)[] = [
    audioTrack!,
    videoTrack!,
  ].filter(Boolean);

  return {
    localTracks,
    getLocalVideoTrack,
    getLocalAudioTrack,
    removeLocalAudioTrack,
    removeLocalVideoTrack,
    getAudioAndVideoTracks,
    isFetchingLocalTracks,
  };
}
